import React, { useRef, useEffect } from "react";
import Card from "./Card";
import styles from './Card.module.css';

// CardContainer
const CardContainer = ({ posts, loading, title = "Products", isWishlistPage = false, onProductClick }) => {
  // Reference to the container element for scrolling
  const containerRef = useRef(null);
  // Variables for touch scrolling
  let startX = 0;
  let startY = 0;
  let scrollLeft = 0;
  let isDragging = false;
  let lastScrollLeft = 0;
  let lastScrollTime = Date.now();
  // Determine container styles based on number of cards and screen size
  const getContainerStyle = () => {
    const isMobile = window.innerWidth <= 768;
    
    // Mobile: Center only if exactly 1 card
    if (isMobile) {
      const shouldCenter = posts && posts.length === 1;
      return {
        margin: '0 auto',
        padding: '16px 0',
        justifyContent: shouldCenter ? 'center' : 'flex-start',
        alignItems: 'stretch',
      };
    }
    
    // Desktop: Center only if 4 or fewer cards
    const shouldCenter = posts && posts.length <= 4;
    return {
      margin: '0 auto',
      padding: '16px 0',
      justifyContent: shouldCenter ? 'center' : 'flex-start',
      alignItems: 'stretch',
    };
  };

  // Enhanced Smooth Touch Scrolling
  const handleTouchStart = (e) => {
    // Initialize touch start positions and scrolling state
    startX = e.touches[0].pageX;
    startY = e.touches[0].pageY;
    scrollLeft = containerRef.current.scrollLeft;
    isDragging = true;
    containerRef.current.style.scrollBehavior = 'auto';
    lastScrollLeft = scrollLeft;
    lastScrollTime = Date.now();
  };
  // Handle touch move for horizontal scrolling
  const handleTouchMove = (e) => {
    if (!isDragging) return;

    // Avoid consoole log warnings
    if (!e.cancelable) return;

    const x = e.touches[0].pageX;
    const y = e.touches[0].pageY;
    const deltaX = Math.abs(x - startX);
    const deltaY = Math.abs(y - startY);

    // If vertical movement is greater, allow page scrolling
    if (deltaY > deltaX) {
      return;
    }

    e.preventDefault();
    const walk = (x - startX) * 1.5;
    if (containerRef.current) {
      containerRef.current.scrollLeft = scrollLeft - walk;
    }
  };
  // Handle touch end to snap to nearest card
  const handleTouchEnd = () => {
    // Snap to nearest card after touch end
    if (!isDragging) return;
    isDragging = false;
    containerRef.current.style.scrollBehavior = 'smooth';

    // Calculate nearest card index
    const container = containerRef.current;
    const isMobile = window.innerWidth <= 768;
    const cardWidth = isMobile ? 160 + 10 : 240 + 20;
    const currentScrollLeft = container.scrollLeft;
    const velocity = Math.abs(currentScrollLeft - lastScrollLeft) / (Date.now() - lastScrollTime);
    // Calculate nearest card index
    let nearestCard = Math.round(currentScrollLeft / cardWidth);
    
    // Add momentum based on velocity
    if (velocity > 0.5) {
      const direction = currentScrollLeft > lastScrollLeft ? 1 : -1;
      nearestCard += Math.round(velocity * direction);
    }
    // Clamp nearestCard within valid range
    const maxCards = Math.floor(container.scrollWidth / cardWidth) - 1;
    nearestCard = Math.max(0, Math.min(nearestCard, maxCards));
    // Scroll to the nearest card
    container.scrollTo({
      left: nearestCard * cardWidth,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    // Attach touch event listeners for enhanced scrolling
    const container = containerRef.current;
    if (container) {
      // Add event listeners
      container.addEventListener('touchstart', handleTouchStart, { passive: false });
      container.addEventListener('touchmove', handleTouchMove, { passive: false });
      container.addEventListener('touchend', handleTouchEnd);

      return () => {
        // Clean up event listeners on unmount
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, []);

  return (
    <>
      <h1 style={{ position: 'relative', margin: '2% 5% -5% 5%'}} data-aos="fade-up" data-aos-delay="100">
        {typeof title === 'string' ? title : (title.name || 'Category')}
      </h1>
      <div className={styles.cardContainerWrapper} style={{ position: 'relative', width: '100%' }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center center',
          backgroundSize: 'contain',
          backgroundAttachment: 'fixed',
          opacity: 0.25,
          zIndex: 0,
          pointerEvents: 'none',
        }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div
            ref={containerRef}
            className={styles.cardContainer}
            style={getContainerStyle()}
            data-aos="fade-up"
            data-aos-delay="200"
          >
            {loading ? (
              <p data-aos="fade-in" data-aos-delay="300">Loading...</p>
            ) : posts && posts.length > 0 ? (
              posts.map((post, index) => (
                <Card
                  key={`${post._id}-${index}`}
                  post={post}
                  isWishlistPage={isWishlistPage}
                  onClick={() => onProductClick ? onProductClick(post) : null}
                />
              ))
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh', width: '100%' }} data-aos="fade-up" data-aos-delay="300">
                <h3 className="empty-text" style={{ color: 'red' }}>No items found</h3>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CardContainer;